/**
 * @file routes/_app/certified-orgs.tsx
 * @description 獲證組織名錄列表頁（客戶 PPT S6：擴欄 + 自動匯總 + 表頭篩選 + 連任預警）。
 *
 * - 名錄為發證流程自動匯總的**只讀**正式名錄（給主管機構），前端無新增/編輯入口；
 *   點列可開詳情 Drawer 查看全部欄位。
 * - 表頭篩選（Excel autofilter 式）：低基數欄用 antd Table filters、
 *   文字欄用列頭搜索（filterDropdown + Input），全部走後端 query 參數過濾。
 * - 欄位極多：左側固定關鍵列 + 橫向滾動 + 「列設置」Popover（勾選持久化 localStorage）。
 * - 稽核組連任 3 年 → 紅色「連任3年」Tag，可篩選。
 */
import { createFileRoute } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Popover,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { message } from '@/lib/antd-app';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { FilterValue } from 'antd/es/table/interface';
import type { TablePaginationConfig } from 'antd';
import { Search, Eye, Download, AlertTriangle, RefreshCw, Settings2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import {
  DIRECTORY_CATEGORIES,
  DIRECTORY_CATEGORY_LABELS,
  DIRECTORY_STATUS_LABELS,
  DIRECTORY_VERIFICATION_TYPES,
  ORGANIC_STAGES,
  daysSinceDate,
  formatProcessingSites,
} from '@zhcb/shared';
import type { DirectoryCategory, DirectoryStatus } from '@zhcb/shared';
import {
  useDirectoryList,
  type DirectoryItem,
  type DirectoryListFilter,
} from '@/hooks/useCertifiedOrgDirectory';
import {
  ListPageShell,
  PageMetaItem,
  FilterBar,
  FilterBarLabel,
  StatusPill,
  type StatusPillProps,
} from '@/components/shell';
import { DirectoryDetailDrawer } from '@/components/certified-orgs/DirectoryDetailDrawer';
import { AllClientsRosterTable } from '@/components/certified-orgs/AllClientsRosterTable';
import { useAuthStore } from '@/lib/auth-context';
import { userCan } from '@zhcb/shared';
import { color, font, text, weight } from '@/styles/tokens';

export const Route = createFileRoute('/_app/certified-orgs')({
  component: CertifiedOrgsPage,
});

const statusToneMap: Record<DirectoryStatus, { label: string; tone: StatusPillProps['tone'] }> = {
  active: { label: '有效', tone: 'success' },
  suspended: { label: '暫停', tone: 'warning' },
  terminated: { label: '已終止', tone: 'danger' },
  archived: { label: '已歸檔', tone: 'neutral' },
};

/** 列設置持久化 key（存「隱藏」的列 key 陣列） */
const HIDDEN_COLS_STORAGE_KEY = 'zhcb-certified-orgs-hidden-cols';

/** 預設隱藏的列（欄位太多，先展示核心欄，其餘由「列設置」開啟） */
const DEFAULT_HIDDEN_COLS = [
  'verificationStandard',
  'verificationOption',
  'verificationScope',
  'completedYears',
  'completedTypes',
  'firstIssuedAt',
  'validFrom',
  'phone',
  'postalCode',
  'shippingAddress',
  'mainParcelAddress',
  'primaryLandNo',
  'processingSites',
  'channels',
  'plannedAuditorNext',
  'planNo',
  'auditorAvoidance',
  'pendingTermination',
  'terminationDocStatus',
  'paperReportArchived',
  'verificationGroupNote',
  'clientNote',
  'adminGroupNote',
  'dispatchNote',
];

/** 讀 localStorage 的隱藏列設定；無/損壞回預設 */
function loadHiddenCols(): string[] {
  try {
    const raw = localStorage.getItem(HIDDEN_COLS_STORAGE_KEY);
    if (!raw) return DEFAULT_HIDDEN_COLS;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as string[]) : DEFAULT_HIDDEN_COLS;
  } catch {
    return DEFAULT_HIDDEN_COLS;
  }
}

/** 文字欄表頭篩選使用的欄 key（對應後端 ilike query 參數） */
type TextFilterKey =
  | 'registrationNo'
  | 'certificateNo'
  | 'clientName'
  | 'legalRepresentative'
  | 'currentStatus';

function CertifiedOrgsPage() {
  const [detailId, setDetailId] = useState<number | null>(null);
  const [category, setCategory] = useState<DirectoryCategory | undefined>();
  const [status, setStatus] = useState<DirectoryStatus | undefined>();
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // 表頭篩選狀態（低基數欄 + 文字欄），統一映射為後端 query 參數
  const [headerFilters, setHeaderFilters] = useState<
    Pick<
      DirectoryListFilter,
      'rotationWarning' | 'pendingTermination' | 'verificationType' | 'organicStage' | TextFilterKey
    >
  >({});
  // 列設置：隱藏列 key（localStorage 持久化）
  const [hiddenCols, setHiddenCols] = useState<string[]>(loadHiddenCols);

  const { data, isLoading } = useDirectoryList({
    page,
    pageSize,
    category,
    status,
    search: searchText || undefined,
    ...headerFilters,
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const byCat = useMemo(() => {
    const m: Record<DirectoryCategory, number> = { organic: 0, tap: 0, organic_branch: 0 };
    items.forEach((i) => (m[i.category] += 1));
    return m;
  }, [items]);

  const expiringCount = items.filter((i) => {
    if (!i.validUntil) return false;
    const days = (new Date(i.validUntil).getTime() - Date.now()) / 86_400_000;
    return days > 0 && days <= 90;
  }).length;
  const rotationCount = items.filter((i) => i.rotationWarning).length;

  const handleExport = () => {
    if (!category) {
      message.warning('請先選擇類別再匯出');
      return;
    }
    window.location.href = `/api/certified-org-directory/export.xlsx?category=${category}`;
  };

  const persistHiddenCols = useCallback((next: string[]) => {
    setHiddenCols(next);
    try {
      localStorage.setItem(HIDDEN_COLS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage 不可用（如隱私模式）時僅生效於本次會話
    }
  }, []);

  /** 設置文字欄篩選值（空字串視為清除） */
  const setTextFilter = useCallback((key: TextFilterKey, value: string) => {
    setPage(1);
    setHeaderFilters((prev) => ({ ...prev, [key]: value || undefined }));
  }, []);

  /** 產生「列頭搜索」filterDropdown（Excel autofilter 式文字欄過濾，走後端 ilike） */
  const columnSearchProps = useCallback(
    (key: TextFilterKey): Partial<ColumnType<DirectoryItem>> => ({
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input.Search
            placeholder="搜索…"
            defaultValue={headerFilters[key] ?? ''}
            allowClear
            autoFocus
            onSearch={(v) => {
              setTextFilter(key, v.trim());
              confirm();
            }}
            style={{ width: 200 }}
          />
        </div>
      ),
      filterIcon: () => (
        <Search size={13} color={headerFilters[key] ? color.brand.primary : undefined} />
      ),
      // 有值時讓列頭圖標保持高亮
      filteredValue: headerFilters[key] ? [headerFilters[key]] : null,
      // 過濾在後端完成，前端不再本地過濾
      onFilter: () => true,
    }),
    [headerFilters, setTextFilter],
  );

  // ── 全部列定義（key 必填；hidden 由列設置控制） ─────────────────────────────
  const allColumns: ColumnsType<DirectoryItem> = useMemo(() => {
    const cols: ColumnsType<DirectoryItem> = [
      {
        title: '類別',
        key: 'category',
        dataIndex: 'category',
        width: 96,
        fixed: 'left',
        render: (v: DirectoryCategory) => (
          <StatusPill tone="info" size="sm" variant="outline">
            {DIRECTORY_CATEGORY_LABELS[v]}
          </StatusPill>
        ),
      },
      {
        title: '驗證註冊號',
        key: 'registrationNo',
        dataIndex: 'registrationNo',
        width: 130,
        fixed: 'left',
        ...columnSearchProps('registrationNo'),
        render: (v: string) => (
          <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
            {v}
          </span>
        ),
      },
      {
        title: '農產品經營者名稱',
        key: 'clientName',
        dataIndex: 'clientName',
        width: 180,
        fixed: 'left',
        ...columnSearchProps('clientName'),
        render: (v: string, r) => (
          <Space>
            <span style={{ fontWeight: weight.medium }}>{v}</span>
            {r.paymentWarning && (
              <Tooltip title="欠費警示">
                <AlertTriangle size={14} color="#ef4444" />
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: '證書字號',
        key: 'certificateNo',
        dataIndex: 'certificateNo',
        width: 140,
        ...columnSearchProps('certificateNo'),
        render: (v) => v || '—',
      },
      {
        title: '法定負責人',
        key: 'legalRepresentative',
        dataIndex: 'legalRepresentative',
        width: 100,
        ...columnSearchProps('legalRepresentative'),
        render: (v) => v || '—',
      },
      {
        title: '驗證標準',
        key: 'verificationStandard',
        dataIndex: 'verificationStandard',
        width: 160,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '驗證類別',
        key: 'verificationType',
        dataIndex: 'verificationType',
        width: 100,
        filters: DIRECTORY_VERIFICATION_TYPES.map((t) => ({ text: t, value: t })),
        filterMultiple: false,
        filteredValue: headerFilters.verificationType ? [headerFilters.verificationType] : null,
        render: (v) => v || '—',
      },
      {
        title: '驗證選項',
        key: 'verificationOption',
        dataIndex: 'verificationOption',
        width: 100,
        render: (v) => v || '—',
      },
      {
        title: '驗證範圍',
        key: 'verificationScope',
        dataIndex: 'verificationScope',
        width: 160,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '有機/轉型',
        key: 'organicStage',
        dataIndex: 'organicStage',
        width: 100,
        filters: ORGANIC_STAGES.map((s) => ({ text: s, value: s })),
        filterMultiple: false,
        filteredValue: headerFilters.organicStage ? [headerFilters.organicStage] : null,
        render: (v) => v || '—',
      },
      { title: '驗證面積', key: 'area', dataIndex: 'area', width: 90, render: (v) => v || '—' },
      {
        title: '農戶數',
        key: 'memberCount',
        dataIndex: 'memberCount',
        width: 70,
        render: (v) => v ?? '—',
      },
      {
        title: '已完成驗證年度',
        key: 'completedYears',
        dataIndex: 'completedYears',
        width: 140,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '已完成驗證類型',
        key: 'completedTypes',
        dataIndex: 'completedTypes',
        width: 160,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '初次發證日期',
        key: 'firstIssuedAt',
        dataIndex: 'firstIssuedAt',
        width: 110,
        render: (v: string | null) => v || '—',
      },
      {
        title: '最新發證日期',
        key: 'latestIssuedAt',
        dataIndex: 'latestIssuedAt',
        width: 110,
        render: (v: string | null) => v || '—',
      },
      {
        // 需求 7：距離上次發證天數 = now - latestIssuedAt，列表即時計算不落庫
        title: '距上次發證(天)',
        key: 'daysSinceLastIssueNow',
        width: 120,
        render: (_, r) => daysSinceDate(r.latestIssuedAt) ?? '—',
      },
      {
        title: '發證有效期間(起)',
        key: 'validFrom',
        dataIndex: 'validFrom',
        width: 110,
        render: (v: string | null) => v || '—',
      },
      {
        title: '證書到期日',
        key: 'validUntil',
        dataIndex: 'validUntil',
        width: 110,
        render: (v: string | null) => v || '—',
      },
      {
        title: '年度',
        key: 'currentYear',
        dataIndex: 'currentYear',
        width: 70,
        render: (v) => v || '—',
      },
      {
        title: '驗證狀態',
        key: 'currentStatus',
        dataIndex: 'currentStatus',
        width: 90,
        ...columnSearchProps('currentStatus'),
        render: (v) => v || '—',
      },
      {
        // 序126②：「稽核組(Y1~Y4)」改名「主導稽核員」（歷年主導稽核員，可看是否連任3年）
        title: '主導稽核員',
        key: 'auditors',
        width: 220,
        render: (_, r) => {
          const parts = [r.auditorY1, r.auditorY2, r.auditorY3, r.auditorY4].filter(Boolean);
          return parts.length ? parts.join(' / ') : '—';
        },
      },
      {
        title: '連任預警',
        key: 'rotationWarning',
        dataIndex: 'rotationWarning',
        width: 100,
        filters: [
          { text: '連任3年', value: 'true' },
          { text: '正常', value: 'false' },
        ],
        filterMultiple: false,
        filteredValue:
          headerFilters.rotationWarning === undefined
            ? null
            : [String(headerFilters.rotationWarning)],
        render: (v: boolean) => (v ? <Tag color="red">連任3年</Tag> : '—'),
      },
      {
        // 序126②①：預排稽核員（次年度預排的主導稽核員）
        title: '預排稽核員',
        key: 'plannedAuditorNext',
        dataIndex: 'plannedAuditorNext',
        width: 110,
        render: (v) => v || '—',
      },
      {
        // 序126②①：預派案計劃編號（派案時生成的稽核計劃編號）
        title: '預派案計劃編號',
        key: 'planNo',
        dataIndex: 'planNo',
        width: 150,
        render: (v) => v || '—',
      },
      {
        // 序126②①：稽核員利益回避（人工標記）
        title: '稽核員利益回避',
        key: 'auditorAvoidance',
        dataIndex: 'auditorAvoidance',
        width: 160,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        // 序126②③：客戶要求刪除「輔導員」列（欄位保留於 DB / 後端相容，僅移除名錄展示）
        title: '通路',
        key: 'channels',
        dataIndex: 'channels',
        width: 130,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        // 序126②④：「連絡電話」改「聯絡電話」
        title: '聯絡電話',
        key: 'phone',
        dataIndex: 'phone',
        width: 120,
        render: (v) => v || '—',
      },
      {
        // 序96.2：戶籍地址（directory.registered_address）
        title: '戶籍地址',
        key: 'registeredAddress',
        dataIndex: 'registeredAddress',
        width: 180,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        // 序96.2：通訊地址（directory.mailing_address）
        title: '通訊地址',
        key: 'mailingAddress',
        dataIndex: 'mailingAddress',
        width: 180,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '郵遞區號',
        key: 'postalCode',
        dataIndex: 'postalCode',
        width: 84,
        render: (v) => v || '—',
      },
      {
        title: '寄件地址',
        key: 'shippingAddress',
        dataIndex: 'shippingAddress',
        width: 180,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '主要地號',
        key: 'primaryLandNo',
        dataIndex: 'primaryLandNo',
        width: 130,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '驗證場所地址(主要地號)',
        key: 'mainParcelAddress',
        dataIndex: 'mainParcelAddress',
        width: 180,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '採收後處理場所名稱+地址+作業項目',
        key: 'processingSites',
        dataIndex: 'processingSites',
        width: 180,
        ellipsis: true,
        render: (v) => formatProcessingSites(v) || '—',
      },
      {
        title: '待停止/結束',
        key: 'pendingTermination',
        dataIndex: 'pendingTermination',
        width: 104,
        filters: [
          { text: '待停止/結束', value: 'true' },
          { text: '正常', value: 'false' },
        ],
        filterMultiple: false,
        filteredValue:
          headerFilters.pendingTermination === undefined
            ? null
            : [String(headerFilters.pendingTermination)],
        render: (v: boolean) => (v ? <Tag color="orange">待停止/結束</Tag> : '—'),
      },
      {
        title: '結束申請書、切結書是否已給對方',
        key: 'terminationDocStatus',
        dataIndex: 'terminationDocStatus',
        width: 110,
        render: (v) => v || '—',
      },
      {
        title: '紙本報告歸檔',
        key: 'paperReportArchived',
        dataIndex: 'paperReportArchived',
        width: 88,
        render: (v: boolean | null) => (v == null ? '—' : v ? '✓' : '—'),
      },
      {
        title: '驗證組備註',
        key: 'verificationGroupNote',
        dataIndex: 'verificationGroupNote',
        width: 160,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '客戶備註',
        key: 'clientNote',
        dataIndex: 'clientNote',
        width: 160,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '管理組備註',
        key: 'adminGroupNote',
        dataIndex: 'adminGroupNote',
        width: 160,
        ellipsis: true,
        render: (v) => v || '—',
      },
      {
        title: '派案注意事項',
        key: 'dispatchNote',
        dataIndex: 'dispatchNote',
        width: 160,
        ellipsis: true,
        render: (v) => v || '—',
      },
    ];

    // 類別專屬列（僅在對應類別過濾時追加）
    if (category === 'tap') {
      cols.push(
        {
          title: '產業別',
          key: 'industryType',
          dataIndex: 'industryType',
          width: 100,
          render: (v) => v || '—',
        },
        {
          title: '產品類別',
          key: 'productCategory',
          dataIndex: 'productCategory',
          width: 110,
          render: (v) => v || '—',
        },
        {
          // 序126②⑤：產銷履歷 TAP 用「驗證品項」（名詞區分：TAP=驗證品項 / TOP=產品品項）
          title: '驗證品項',
          key: 'productItems',
          dataIndex: 'productItems',
          width: 160,
          ellipsis: true,
          render: (v) => v || '—',
        },
        {
          // 序126②⑤：TAP「L1上傳狀態」
          title: 'L1上傳狀態',
          key: 'l1Uploaded',
          dataIndex: 'l1Uploaded',
          width: 100,
          render: (v: boolean | null) => (v == null ? '—' : v ? '已上傳' : '未上傳'),
        },
        {
          title: '產銷履歷編號',
          key: 'tapCode',
          dataIndex: 'tapCode',
          width: 130,
          render: (v) => v || '—',
        },
        {
          // 序126②⑤：TAP 新增「標章使用」列
          title: '標章使用',
          key: 'labelUsage',
          dataIndex: 'labelUsage',
          width: 110,
          ellipsis: true,
          render: (v) => v || '—',
        },
      );
    } else if (category === 'organic' || category === 'organic_branch') {
      cols.push(
        {
          title: '驗證行為',
          key: 'verificationBehavior',
          dataIndex: 'verificationBehavior',
          width: 110,
          render: (v) => v || '—',
        },
        {
          // 序126②⑥：有機 TOP 用「產品品項」（名詞區分：TOP=產品品項 / TAP=驗證品項）
          title: '產品品項',
          key: 'verifiedItems',
          dataIndex: 'verifiedItems',
          width: 160,
          ellipsis: true,
          render: (v) => v || '—',
        },
      );
    }

    cols.push(
      {
        title: '狀態',
        key: 'status',
        dataIndex: 'status',
        width: 90,
        render: (v: DirectoryStatus) => {
          const d = statusToneMap[v];
          return (
            <StatusPill tone={d.tone} size="sm" dot>
              {d.label}
            </StatusPill>
          );
        },
      },
      {
        title: '操作',
        key: '_actions',
        width: 64,
        fixed: 'right',
        render: (_, record) => (
          <Tooltip title="詳情">
            <Button
              type="text"
              size="small"
              icon={<Eye size={14} />}
              onClick={() => setDetailId(record.id)}
            />
          </Tooltip>
        ),
      },
    );
    return cols;
  }, [category, columnSearchProps, headerFilters]);

  /** 不可隱藏的關鍵列（固定列 + 操作列） */
  const lockedKeys = useMemo(() => ['category', 'registrationNo', 'clientName', '_actions'], []);

  const visibleColumns = useMemo(
    () =>
      allColumns.filter(
        (c) => lockedKeys.includes(String(c.key)) || !hiddenCols.includes(String(c.key)),
      ),
    [allColumns, hiddenCols, lockedKeys],
  );

  /** Table onChange：分頁 + 低基數欄 filters → 後端 query 參數 */
  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
  ) => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 50);
    const first = (k: string) => filters[k]?.[0];
    setHeaderFilters((prev) => ({
      ...prev,
      verificationType: (first('verificationType') as string | undefined) ?? undefined,
      organicStage: (first('organicStage') as string | undefined) ?? undefined,
      rotationWarning:
        first('rotationWarning') === undefined ? undefined : first('rotationWarning') === 'true',
      pendingTermination:
        first('pendingTermination') === undefined
          ? undefined
          : first('pendingTermination') === 'true',
    }));
  };

  const hasAnyFilter =
    !!searchText ||
    !!category ||
    !!status ||
    Object.values(headerFilters).some((v) => v !== undefined);

  // 序95：名錄視圖切換——獲證組織名錄 / 全部註冊客戶名錄（後者僅管理組可見，
  // 因 clients 列表端點需 client.manage 能力）
  const authUser = useAuthStore((st) => st.user);
  const canViewAllClients = authUser ? userCan(authUser, 'client.manage') : false;
  const [rosterView, setRosterView] = useState<'directory' | 'all-clients'>('directory');

  // 列設置 Popover 內容
  const columnSettingsContent = (
    <div style={{ maxHeight: 360, overflowY: 'auto', width: 220 }}>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
        <Button size="small" type="link" onClick={() => persistHiddenCols([])}>
          全部顯示
        </Button>
        <Button size="small" type="link" onClick={() => persistHiddenCols(DEFAULT_HIDDEN_COLS)}>
          恢復預設
        </Button>
      </div>
      {allColumns
        .filter((c) => !lockedKeys.includes(String(c.key)))
        .map((c) => {
          const key = String(c.key);
          return (
            <div key={key} style={{ padding: '2px 0' }}>
              <Checkbox
                checked={!hiddenCols.includes(key)}
                onChange={(e) => {
                  persistHiddenCols(
                    e.target.checked ? hiddenCols.filter((k) => k !== key) : [...hiddenCols, key],
                  );
                }}
              >
                {typeof c.title === 'string' ? c.title : key}
              </Checkbox>
            </div>
          );
        })}
    </div>
  );

  return (
    <>
      <ListPageShell
        title="獲證組織名錄"
        // 2026-09-12 回報序11：頁名由「組織名錄」改回「獲證組織名錄」，視圖「全部註冊客戶名錄」改為「全部客戶名錄」
        description="獲證組織正式名錄與全部客戶名錄；資料由發證流程自動匯總。"
        meta={
          <>
            <PageMetaItem label="總數" value={`${total} 筆`} />
            {DIRECTORY_CATEGORIES.map((c) => (
              <PageMetaItem key={c} label={DIRECTORY_CATEGORY_LABELS[c]} value={`${byCat[c]} 筆`} />
            ))}
            <PageMetaItem
              icon={<Badge status="warning" />}
              label="90日內到期"
              value={`${expiringCount} 筆`}
            />
            <PageMetaItem
              icon={<Badge status="error" />}
              label="連任3年預警"
              value={`${rotationCount} 筆`}
            />
          </>
        }
        actions={
          <Space>
            {canViewAllClients && (
              <Segmented
                value={rosterView}
                onChange={(v) => setRosterView(v as 'directory' | 'all-clients')}
                options={[
                  { value: 'directory', label: '獲證組織名錄' },
                  { value: 'all-clients', label: '全部客戶名錄' },
                ]}
              />
            )}
            {/* 序221：類別/狀態/列設置/匯出 皆屬「組織名錄」(獲證名錄) 視圖專用——
                category 是這組篩選的核心參數，套到「全部註冊客戶名錄」view 上毫無意義，
                點擊匯出必觸發「請先選擇類別」錯誤。全部客戶名錄視圖改用自己內建的搜尋+匯出。 */}
            {rosterView === 'directory' && (
              <>
                <Popover
                  content={columnSettingsContent}
                  title="列設置"
                  trigger="click"
                  placement="bottomRight"
                >
                  <Button icon={<Settings2 size={14} />}>列設置</Button>
                </Popover>
                <Button icon={<Download size={14} />} onClick={handleExport}>
                  匯出 Excel
                </Button>
              </>
            )}
          </Space>
        }
        filters={
          rosterView === 'all-clients' ? undefined : (
            <FilterBar
              search={
                <Input
                  placeholder="搜尋 受評者 / 註冊號 / 證書號"
                  prefix={<Search size={14} color={color.text.muted} />}
                  value={searchText}
                  onChange={(e) => {
                    setPage(1);
                    setSearchText(e.target.value);
                  }}
                  allowClear
                />
              }
              filters={
                <>
                  <FilterBarLabel>類別</FilterBarLabel>
                  <Select
                    placeholder="全部類別"
                    value={category}
                    onChange={(v) => {
                      setPage(1);
                      setCategory(v);
                    }}
                    allowClear
                    style={{ width: 140 }}
                    options={DIRECTORY_CATEGORIES.map((c) => ({
                      value: c,
                      label: DIRECTORY_CATEGORY_LABELS[c],
                    }))}
                  />
                  <FilterBarLabel>狀態</FilterBarLabel>
                  <Select
                    placeholder="全部狀態"
                    value={status}
                    onChange={(v) => {
                      setPage(1);
                      setStatus(v);
                    }}
                    allowClear
                    style={{ width: 140 }}
                    options={Object.entries(DIRECTORY_STATUS_LABELS).map(([v, label]) => ({
                      value: v,
                      label,
                    }))}
                  />
                </>
              }
              extra={
                hasAnyFilter && (
                  <Button
                    type="text"
                    size="small"
                    icon={<RefreshCw size={13} />}
                    onClick={() => {
                      setSearchText('');
                      setCategory(undefined);
                      setStatus(undefined);
                      setHeaderFilters({});
                      setPage(1);
                    }}
                  >
                    重置
                  </Button>
                )
              }
            />
          )
        }
      >
        {rosterView === 'all-clients' ? (
          /* 序95：全部註冊客戶名錄（所有已具驗證註冊碼的客戶，含新申請/結束/終止等狀態） */
          <AllClientsRosterTable />
        ) : (
          <Table<DirectoryItem>
            rowKey="id"
            columns={visibleColumns}
            dataSource={items}
            loading={isLoading}
            scroll={{ x: 'max-content' }}
            // 序10（2026-09-14 回報「列表沒有軸線」）：名錄欄位多、表格寬於畫面，原生橫向捲軸
            // 在表格最底部，且 macOS「捲動時才顯示捲軸」/ 淺色 thumb 下幾乎看不見，使用者無從
            // 橫向捲動。改用 antd sticky：表頭吸附在 56px 應用頂欄下方、橫向捲軸吸附視窗底部常駐。
            sticky={{ offsetHeader: 56 }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => setDetailId(record.id),
              style: { cursor: 'pointer' },
            })}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (t) => `共 ${t} 筆`,
            }}
          />
        )}
      </ListPageShell>

      <DirectoryDetailDrawer
        id={detailId}
        open={detailId !== null}
        onClose={() => setDetailId(null)}
      />
    </>
  );
}
